import { useEffect, useState } from "react";
import { getData } from "../../../services/library";
import Navbar from "../../Navbar";
import { 
  Button, 
  Container, 
  IconButton, 
  TextField, 
  Tooltip, 
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  Box,
  Chip
} from "@mui/material";
import { useFechas } from "../pedidosEnvasado/context/FechaContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';

function CsvQR() {
  const [sucursales, setSucursales] = useState([]);
  const [filteredSucursales, setFilteredSucursales] = useState([]);
  const [csvgeneral, setCsvgeneral] = useState([]);
  const [csvsucursal, setCsvsucursal] = useState([]); // Nuevo estado para CSV por sucursal
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("nombre");
  const [order, setOrder] = useState("asc");
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [loadingSucursal, setLoadingSucursal] = useState(false); // Estado para controlar carga
  
  const { desde, hasta, setDesde, setHasta } = useFechas();

  const obtenerSucursales = () => {
    getData("/sucursales", (data) => {
      setSucursales(data);
      setFilteredSucursales(data);
    });
  };

  const obtenerCsvGeneral = () => {
    getData("/envasado_pagos_combinados/csvgeneral/" +
      desde +
      " 00:00:00/" +
      hasta +
      " 23:59:59/", (data) => {
        setCsvgeneral(data);
      });
  };

  // Función para obtener CSV por sucursal
  const obtenerCsvPorSucursal = (idSucursal, nombreSucursal) => {
    setLoadingSucursal(true);
    getData("/envasado_pagos_combinados/csvxsucursal/" +
      desde +
      " 00:00:00/" +
      hasta +
      " 23:59:59/" +
      idSucursal, (data) => {
        setCsvsucursal(data);
        setLoadingSucursal(false);
        // Una vez que tenemos los datos, descargamos el CSV
        handleDescargarCSVSucursal(data, nombreSucursal);
      }, (error) => {
        console.error("Error al obtener CSV por sucursal:", error);
        alert("Error al descargar los datos de la sucursal");
        setLoadingSucursal(false);
      });
  };

  useEffect(() => {
    obtenerSucursales();
    obtenerCsvGeneral();
  }, []);

  useEffect(() => {
    const filtered = sucursales.filter((sucursal) =>
      sucursal.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSucursales(filtered);
    setPage(0);
  }, [searchTerm, sucursales]);

  const handleUpdate = () => {
    obtenerSucursales();
    obtenerCsvGeneral();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVerDetalle = (sucursal) => {
    setSelectedSucursal(sucursal);
    setOpenDetail(true);
    // console.log("Ver detalle de:", sucursal);
  };

  // Función para descargar CSV por sucursal
  const handleDescargarCSV = (sucursal) => {
    // console.log("Descargar CSV de:", sucursal);
    if (!sucursal || !sucursal.id) {
      alert("No se pudo identificar la sucursal");
      return;
    }
    obtenerCsvPorSucursal(sucursal.id, sucursal.nombre);
  };

  // Función para generar y descargar el CSV de una sucursal específica
  const handleDescargarCSVSucursal = (data, nombreSucursal) => {
    if (!data || data.length === 0) {
      alert(`No hay datos para la sucursal ${nombreSucursal}`);
      return;
    }

    // Definir los campos a incluir en el CSV (mismos que el general)
    const campos = [
      'sucursal',
      'fecha_pedido',
      'chofer',
      'cliente',
      'pedido',
      'importe',
      'fecha_registro_pago',
      'rendicion'
    ];

    // Crear encabezados del CSV
    const encabezados = campos.map(campo => 
      campo.split('_').map(palabra => 
        palabra.charAt(0).toUpperCase() + palabra.slice(1)
      ).join(' ')
    ).join(',');

    // Crear filas de datos
    const filas = data.map(item => {
      return campos.map(campo => {
        let valor = item[campo] !== null && item[campo] !== undefined ? item[campo] : '';
        
        // Formatear fechas
        if (campo.includes('fecha') && valor) {
          valor = formatearFecha(valor);
        }
        
        // Escapar comas y comillas si es necesario
        if (typeof valor === 'string' && (valor.includes(',') || valor.includes('"'))) {
          valor = `"${valor.replace(/"/g, '""')}"`;
        }
        
        return valor;
      }).join(',');
    }).join('\n');

    // Combinar encabezados y filas
    const contenidoCSV = [encabezados, filas].join('\n');

    // Crear y descargar el archivo
    const blob = new Blob(['\uFEFF' + contenidoCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fechaDesde = desde ? formatearFecha(desde) : 'inicio';
    const fechaHasta = hasta ? formatearFecha(hasta) : 'fin';
    const nombreArchivo = nombreSucursal ? nombreSucursal.replace(/\s+/g, '_') : 'sucursal';
    
    link.href = url;
    link.setAttribute('download', `csv_${nombreArchivo}_${fechaDesde}_${fechaHasta}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Función para formatear fecha
  const formatearFecha = (fechaString) => {
    if (!fechaString) return '';
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  };

  // Función para descargar CSV general
  const handleDescargarCSVGeneral = () => {
    if (!csvgeneral || csvgeneral.length === 0) {
      alert('No hay datos para descargar');
      return;
    }

    // Definir los campos a incluir en el CSV
    const campos = [
      'sucursal',
      'fecha_pedido',
      'chofer',
      'cliente',
      'pedido',
      'importe',
      'fecha_registro_pago',
      'rendicion'
    ];

    // Crear encabezados del CSV
    const encabezados = campos.map(campo => 
      campo.split('_').map(palabra => 
        palabra.charAt(0).toUpperCase() + palabra.slice(1)
      ).join(' ')
    ).join(',');

    // Crear filas de datos
    const filas = csvgeneral.map(item => {
      return campos.map(campo => {
        let valor = item[campo] !== null && item[campo] !== undefined ? item[campo] : '';
        
        // Formatear fechas
        if (campo.includes('fecha') && valor) {
          valor = formatearFecha(valor);
        }
        
        // Escapar comas y comillas si es necesario
        if (typeof valor === 'string' && (valor.includes(',') || valor.includes('"'))) {
          valor = `"${valor.replace(/"/g, '""')}"`;
        }
        
        return valor;
      }).join(',');
    }).join('\n');

    // Combinar encabezados y filas
    const contenidoCSV = [encabezados, filas].join('\n');

    // Crear y descargar el archivo
    const blob = new Blob(['\uFEFF' + contenidoCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fechaDesde = desde ? formatearFecha(desde) : 'inicio';
    const fechaHasta = hasta ? formatearFecha(hasta) : 'fin';
    
    link.href = url;
    link.setAttribute('download', `csv_general_${fechaDesde}_${fechaHasta}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const dataToShow = filteredSucursales?.length > 0 
    ? stableSort(filteredSucursales, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  return (
    <div>
      <Navbar />
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2, px: { xs: 2, sm: 3 } }}>

        {/* Barra de herramientas superior */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 1.5, 
            mb: 2, 
            borderRadius: 2,
            backgroundColor: '#f8f9fa'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'nowrap',
            width: '100%'
          }}>
            {/* Lado izquierdo - Título y buscador */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flex: '1 1 auto'
            }}>
              {/* Título y contador */}
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 'fit-content' }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#2471A3', 
                  fontSize: '1.2rem', 
                  whiteSpace: 'nowrap' 
                }}>
                  Sucursales
                </Typography>
                <Chip 
                  label={filteredSucursales?.length || 0} 
                  size="medium" 
                  sx={{ ml: 1, fontSize: '0.85rem', height: 26, fontWeight: 500 }}
                  color="primary"
                />
              </Box>

              {/* Buscador */}
              <TextField
                placeholder="Buscar sucursal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1.2rem', color: '#757575' }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    fontSize: '0.95rem',
                    borderRadius: 2
                  }
                }}
                sx={{ 
                  backgroundColor: '#ffffff',
                  minWidth: '280px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px'
                  }
                }}
              />
            </Box>

            {/* Lado derecho - Filtros y botones */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              flex: '1 1 auto',
              justifyContent: 'flex-end'
            }}>
              {/* Filtros de fecha */}
              <TextField
                size="small"
                label="Desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{ 
                  '& .MuiInputBase-root': { 
                    fontSize: '0.9rem', 
                    height: '40px' 
                  },
                  '& .MuiInputLabel-root': { 
                    fontSize: '0.9rem'
                  },
                  minWidth: '140px'
                }}
              />
              
              <TextField
                size="small"
                label="Hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{ 
                  '& .MuiInputBase-root': { 
                    fontSize: '0.9rem', 
                    height: '40px' 
                  },
                  '& .MuiInputLabel-root': { 
                    fontSize: '0.9rem'
                  },
                  minWidth: '140px'
                }}
              />

              {/* Botón actualizar */}
              <Tooltip title="Actualizar datos" arrow placement="top">
                <IconButton
                  onClick={handleUpdate}
                  sx={{
                    backgroundColor: "#e0e0e0",
                    "&:hover": { backgroundColor: "#d5d5d5" },
                    height: '40px',
                    width: '40px'
                  }}
                >
                  <RefreshIcon sx={{ color: "#333", fontSize: '1.3rem' }} />
                </IconButton>
              </Tooltip>

              {/* Botón CSV general */}
              <Tooltip title="Descargar CSV general" arrow placement="top">
                <Button 
                  startIcon={<DownloadIcon sx={{ fontSize: '1.2rem' }} />} 
                  variant="contained" 
                  color="primary"
                  onClick={handleDescargarCSVGeneral}
                  sx={{ 
                    borderRadius: 2,
                    fontSize: '0.9rem',
                    py: 1,
                    px: 2,
                    whiteSpace: 'nowrap',
                    height: '40px',
                    fontWeight: 500
                  }}
                >
                  CSV General
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        {/* Tabla con acciones */}
        <TableContainer 
          component={Paper} 
          elevation={3}
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: 'calc(100vh - 280px)',
            minHeight: 400
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: '#2471A3', 
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    py: 1,
                    width: '70%'
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "nombre"}
                    direction={orderBy === "nombre" ? order : "asc"}
                    onClick={() => handleRequestSort("nombre")}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        color: 'white !important',
                        fontSize: '1rem'
                      },
                      color: 'white !important',
                      fontSize: '0.9rem'
                    }}
                  >
                    Sucursal
                  </TableSortLabel>
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    backgroundColor: '#2471A3', 
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    py: 1,
                    width: '30%'
                  }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataToShow.length > 0 ? (
                dataToShow.map((sucursal, index) => (
                  <TableRow 
                    key={sucursal.id || index}
                    hover
                    sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
                      '&:hover': { backgroundColor: '#f0f0f0 !important' },
                    }}
                  >
                    <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}>
                      {sucursal.nombre || 'Sin nombre'}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {/* <Tooltip title="Ver detalle" arrow placement="top">
                          <IconButton 
                            onClick={() => handleVerDetalle(sucursal)}
                            size="small"
                            sx={{ 
                              color: '#2471A3',
                              '&:hover': { backgroundColor: '#e3f2fd' },
                              p: 0.5
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Tooltip> */}
                        
                        <Tooltip title="Descargar CSV" arrow placement="top">
                          <IconButton 
                            onClick={() => handleDescargarCSV(sucursal)}
                            size="small"
                            disabled={loadingSucursal}
                            sx={{ 
                              color: '#2e7d32',
                              '&:hover': { backgroundColor: '#e8f5e9' },
                              p: 0.5,
                              '&.Mui-disabled': {
                                color: '#a5d6a7'
                              }
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <InfoIcon sx={{ fontSize: '2rem', color: '#bdbdbd' }} />
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.9rem' }}>
                        No se encontraron sucursales
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        <Paper elevation={2} sx={{ mt: 0.5, borderRadius: 2 }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredSucursales?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.85rem'
              },
              '& .MuiTablePagination-select': {
                fontSize: '0.85rem',
                py: 0.5
              },
              '& .MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  p: 0.5
                }
              }
            }}
          />
        </Paper>
      </Container>
    </div>
  );
}

export default CsvQR;